import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
  MSAL_GUARD_CONFIG,
} from '@azure/msal-angular';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
  InteractionType,
  PopupRequest,
  RedirectRequest,
  SilentRequest,
} from '@azure/msal-browser';
import { fromUnixTime, subMinutes } from 'date-fns';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, filter, last, switchMap, tap } from 'rxjs/operators';
import { UserPermission } from '../../../model/src/lib/user-permission.model';
import { UserProfile } from '../../../model/src/lib/user-profile.model';
import { isNullOrEmpty } from '../../../util/src/lib/is-null-or-empty';
import { AppConfig } from '../../config/app.config';
import { ApiRbacService } from './api-rbac.service';
import { AuthenticationBaseService } from './authentication-base.service';
import { StorageService } from './storage.service';
import { UserProfileService } from './user-profile.service';
import { ToastService } from './toast.service';
import { ErrorService } from '../error/error.service';
import { LoaderService } from './loader.service';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService extends AuthenticationBaseService {
  aadLoginStateSubject$: BehaviorSubject<boolean | null> = new BehaviorSubject<
    boolean | null
  >(null);
  inRefreshProcess: boolean = false;
  private refreshTokenMsgDisplay: boolean = true;
  readonly MAX_INVALID_GRANT_COUNT = 3;
  readonly MAX_REFRESH_TOKEN_COUNT = 5;

  constructor(
    protected userProfileService: UserProfileService,
    private storageService: StorageService,
    private apiRbacService: ApiRbacService,
    private appConfig: AppConfig,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    protected logger: NGXLogger,
    private msalBroadcastService: MsalBroadcastService,
    private http: HttpClient,
    private toastService: ToastService,
    private errorService: ErrorService,
    private loaderService: LoaderService
  ) {
    super(userProfileService);

    if (this.appConfig.getAzureAD().enabled === true) {
      /*
      這段程式監聽 msalBroadcastService 的 msalSubject$, msalSubject$ 會發出與微軟身份驗證相關的事件。
      當事件類型為 ACQUIRE_TOKEN_SUCCESS 和 LOGIN_SUCCESS 這2種事件時，則進行處理。
      */
      this.msalBroadcastService.msalSubject$
        .pipe(
          filter((msg: EventMessage) => {
            this.logger.info(
              'AuthenticationService - constructor - msalBroadcastService - msalSubject - filter eventType',
              msg
            );
            return [
              EventType.ACQUIRE_TOKEN_SUCCESS,
              EventType.LOGIN_SUCCESS,
            ].includes(msg.eventType);
          })
        )
        .subscribe({
          next: (result: EventMessage) => {
            this.logger.info(
              'AuthenticationService - constructor - msalBroadcastService - msalSubject - result',
              result
            );
            const payload = result.payload as AuthenticationResult;
            this.authService.instance.setActiveAccount(payload.account);
            const idToken = payload.idToken;

            if ([EventType.ACQUIRE_TOKEN_SUCCESS].includes(result.eventType)) {
              this.logger.info(
                'AADAuthenticationService - constructor - msalBroadcastService - msalSubject - setProfileAzureAD'
              );

              this.http
                .get<any>(this.appConfig.getAzureAD().resourceMap.uri)
                .subscribe(({ mail }) => {
                  if (mail) {
                    this.initLoginUserData(mail, idToken, true)
                      .pipe(
                        catchError((error) => {
                          return of(false);
                        })
                      )
                      .subscribe(
                        (isSuccess) => {
                          this.aadLoginStateSubject$.next(isSuccess);
                        },
                        (err) => {
                          this.aadLoginStateSubject$.next(false);
                        }
                      );
                  } else {
                    this.logger.error(
                      'Unable to get email information after login'
                    );
                    this.aadLoginStateSubject$.next(false);
                  }
                });
            }
          },
          error: (error) => {
            throw Error(error);
          },
        });

      /*
      這段程式監聽 msalBroadcastService 的 inProgress$,
      inProgress$ 會發出當前的交互狀態（例如，用戶是否正在進行登入或取得 token 的操作）
      Startup：當一個新的交互操作（例如登入或取得 token）開始時。
      Redirect：當交互操作需要重定向到其他頁面（例如微軟的登入頁面）時。
      Popup：當交互操作需要彈出一個新的視窗（例如彈出的微軟登入視窗）時。
      InProgress：當交互操作正在進行中時。
      None：當交互操作完成時。
      */
      this.msalBroadcastService.inProgress$
        .pipe(
          tap((status: InteractionStatus) =>
            this.logger.info(
              'AuthenticationService - constructor - inProgress - status',
              status
            )
          ),
          filter(
            (status: InteractionStatus) => status === InteractionStatus.None
          )
        )
        .subscribe(() => {
          const activeAccount = this.authService.instance.getActiveAccount();

          if (
            !activeAccount &&
            this.authService.instance.getAllAccounts().length > 0
          ) {
            const accounts = this.authService.instance.getAllAccounts();
            this.authService.instance.setActiveAccount(accounts[0]);
          }
        });
    }
  }

  /**
   * @description 是否已登入之判斷
   */
  get isAuthenticated(): boolean {
    return !isNullOrEmpty(this.storageService.userProfile.getItem());
  }

  /**
   * @description aad 登入流程結果狀態(包含 init 登入者資料)
   */
  get aadLoginState$(): Observable<boolean | null> {
    return this.aadLoginStateSubject$.asObservable();
  }

  /**
   * @description 使用轉導頁面方式登入 azure ad
   */
  private loginWithRedirect(): void {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }

  /**
   * @description 使用 popup 方式登入 azure ad
   */
  private loginWithPopup(): void {
    if (this.msalGuardConfig.authRequest) {
      this.authService
        .loginPopup({ ...this.msalGuardConfig.authRequest } as PopupRequest)
        .subscribe();
    } else {
      this.authService.loginPopup().subscribe();
    }
  }

  /**
   * @description azure ad 登入
   */
  loginAzureAd(): void {
    this.logger.info('Authentication Service - loginAzureAD - begin');
    this.loginWithRedirect();
    this.logger.info('Authentication Service - loginAzureAD - end');
  }

  /**
   * @description 模擬登入，只需輸入 員編
   * @param userAccount 使用者 員編
   * @returns 登入結果
   */
  login(userAccount: string): Observable<boolean> {
    return this.initLoginUserData(userAccount);
  }

  /**
   * @description 登入使用者資料初始化
   * @param userAccount 使用者 員編
   * @param idToken aad login response IdToken
   */
  initLoginUserData(userAccount: string, idToken = '', isAAD = false): Observable<boolean> {
    if (idToken) {
      this.storageService.idToken.setItem(idToken);
    }

    let url = isAAD === true ? `/UserProfile/EmailAddress/${userAccount}` : `/UserProfile/${userAccount}`;
    return this.apiRbacService
      .doGet<UserProfile>(url, new HttpParams(), { 'X-ReturnError': 'true' })
      .pipe(
        switchMap((userProfile) => {
          if (userProfile && userProfile.userNo) {
            // set user profile main duty user job title data
            const mainDuty = userProfile.userJobTitles?.find(
              (job) => job.mainDuty
            );
            if (mainDuty) {
              userProfile.managerUserNo =
                mainDuty.department.managerUserNo || '';
              userProfile.managerUserName =
                mainDuty.department.managerUserName || '';
              userProfile.jobTitleId = mainDuty.jobTitleId || '';
              userProfile.workType = mainDuty.department.workType || -1;
              userProfile.departmentId = mainDuty.department.departmentId || -1;
              userProfile.departmentCode =
                mainDuty.department.departmentCode || '';
              userProfile.departmentName =
                mainDuty.department.departmentName || '';
              userProfile.companyId =
                mainDuty.department.company.companyId.toString() || '';
              userProfile.companyCode =
                mainDuty.department.company.companyCode || '';
              userProfile.companyShortName =
                mainDuty.department.company.companyShortName || '';
              userProfile.companyName =
                mainDuty.department.company.companyName || '';
            }

            // set user role, 內網登入一律視為 sales
            userProfile.extranetUserRole = 0;

            // get user permission data
            const userPermission$ = this.apiRbacService.doGet<UserPermission>(
              `/Permission/User/${userProfile.userNo}`, new HttpParams(), { 'X-ReturnError': 'true' }
            );

            return forkJoin([userPermission$]).pipe(
              switchMap(([userPermissionResponse]) => {
                if (
                  userPermissionResponse &&
                  userPermissionResponse.rolePermissions &&
                  userPermissionResponse.rolePermissions.length > 0
                ) {
                  const systemPermissions =
                    userPermissionResponse.rolePermissions
                      .map((item) => item.systemPermissions)
                      .reduce((acc, item) => {
                        acc.push(...item);
                        return acc;
                      }, []);

                  // login success save to storage
                  this.storageService.userProfile.setItem(userProfile);

                  this.userProfileService.currentUserSubject.next(userProfile);
                  this.userProfileService.currentSystemPermissionsSubject.next(
                    systemPermissions
                  );

                  return of(true);
                } else {
                  this.logger.error(
                    `Unable to get ${userAccount} permission data`
                  );
                  this.initLoginUserDataFail();
                  return of(false);
                }
              })
            );
          } else {
            this.logger.error(`Unable to get ${userAccount} userprofile data`);
            this.initLoginUserDataFail();
            return of(false);
          }
        }),
        catchError((error) => {
          this.logger.error(
            'AuthenticationService initLoginUserData error: ',
            error
          );
          this.initLoginUserDataFail();
          if (this.appConfig.getAzureAD().enabled === true) {
            window.location.reload();
          }
          return of(false);
        })
      );
  }

  /**
   * @description 清除登入資訊
   */
  clearLoginData(): void {
    this.userProfileService.currentUserSubject.next(null);
    this.userProfileService.currentSystemPermissionsSubject.next([]);
    this.storageService.userProfile.removeItem();
    this.storageService.idToken.removeItem();
  }

  /**
   * @description 登出
   */
  logout(): void {
    this.clearLoginData();

    if (this.appConfig.getAzureAD().enabled) {
      if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
        this.authService.logoutPopup();
      }
      if (this.msalGuardConfig.interactionType === InteractionType.Redirect) {
        this.authService.logoutRedirect();
      }
    }
  }

  /**
   * @description refresh token for aad login
   */
  refreshToken(): void {
    this.inRefreshProcess = true;
    this.logger.info('AuthenticationService - refreshToken - begin');
    const account = this.authService.instance.getActiveAccount();
    const requestObj = { forceRefresh: true, account } as SilentRequest;
    this.logger.info(
      'AuthenticationService - refreshToken - acquireTokenSilent',
      requestObj
    );
    this.authService.acquireTokenSilent(requestObj).subscribe({
      error: (err) => {
        // 判斷error如果為BrowserAuthError，不做其他事情
        if (err.name !== 'BrowserAuthError') {
          this.logger.error(err);
          this.loginAzureAd();
        }
      },
    });
    this.logger.info('AuthenticationService - refreshToken - end');

    setTimeout(() => {
      this.inRefreshProcess = false;
    }, 5000);
  }

  /**
   * @description Refresh token for no permission
   */
  refreshTokenForNoPermission$(): Observable<any> {
    this.loaderService.start();
    this.inRefreshProcess = true;
    this.logger.info('AuthenticationService - refreshToken - begin');

    const account = this.authService.instance.getActiveAccount();
    const requestObj = { forceRefresh: true, account } as SilentRequest;
    this.logger.info(
      'AuthenticationService - refreshToken - acquireTokenSilent',
      requestObj
    );

    return this.authService.acquireTokenSilent(requestObj).pipe(
      tap(() => this.handleRefreshTokenSuccess()),
      catchError((err) => this.handleRefreshTokenError(err))
    );
  }

  /**
   * @description Handle refresh token success
   */
  private handleRefreshTokenSuccess() {
    this.logger.info('Refresh Token succeeded.');
    this.resetRefreshState();
    this.setInvalidGrantCount(0);
    const refreshTokenCount = this.getRefreshTokenCount();
    let canRefresh = this.handleRefreshTokenCount(refreshTokenCount);

    if (this.refreshTokenMsgDisplay) {
      this.refreshTokenMsgDisplay = false;
      this.toastService.info('Refresh Token');
      setTimeout(() => {
        this.loaderService.stop();
        this.refreshTokenMsgDisplay = true;
        if (canRefresh) {
          window.location.reload();
        } else {
          this.setRefreshTokenCount(0);
          this.errorService.handleUnAuthorizedError();
        }
      }, 1000);
    }
  }

  /**
   * @description Handle refresh token error
   */
  private handleRefreshTokenError(error: any): Observable<never> {
    this.logger.error('Refresh Token failed.', error);
    this.resetRefreshState();

    const invalidGrantCount = this.getInvalidGrantCount();
    let canRefresh = true;

    if (error.errorCode === 'invalid_grant') {
      canRefresh = this.handleInvalidGrantError(invalidGrantCount);
    }

    if (error.name !== 'BrowserAuthError') {
      this.handleNonBrowserAuthError(canRefresh);
    }

    return throwError(() => error);
  }

  /**
   * @description Reset refresh process state
   */
  private resetRefreshState() {
    this.inRefreshProcess = false;
    this.loaderService.stop();
  }

  /**
   * @description Handle non-browserAuthError from refresh token
   */
  private handleNonBrowserAuthError(canRefresh: boolean) {
    canRefresh ? this.loginAzureAd() : this.errorService.handleUnAuthorizedError();
  }

  /**
   * @description Handle invalid_grant error from refresh token
   */
  private handleInvalidGrantError(invalidGrantCount: number): boolean {
    this.setInvalidGrantCount(Math.min(invalidGrantCount + 1, this.MAX_INVALID_GRANT_COUNT));
    return invalidGrantCount < this.MAX_INVALID_GRANT_COUNT;
  }

  /**
   * @description Handle refresh token count
   */
  private handleRefreshTokenCount(refreshTokenCount: number): boolean {
    this.setRefreshTokenCount(Math.min(refreshTokenCount + 1, this.MAX_REFRESH_TOKEN_COUNT));
    return refreshTokenCount < this.MAX_REFRESH_TOKEN_COUNT;
  }

  /**
   * @description Set invalid grant count
   */
  private setInvalidGrantCount(count: number) {
    this.storageService.invalidGrantCount.setItem(count);
  }

  /**
   * @description Get invalid grant count
   */
  private getInvalidGrantCount(): number {
    return this.storageService.invalidGrantCount.getItem() || 0;
  }

  /**
   * @description Set refresh token count
   */
  private setRefreshTokenCount(count: number) {
    this.storageService.refreshCount.setItem(count);
  }

  /**
   * @description Get refresh token count
   */
  private getRefreshTokenCount(): number {
    return this.storageService.refreshCount.getItem() || 0;
  }

  private initLoginUserDataFail(): void {
    this.storageService.userProfile.removeItem();
    this.storageService.idToken.removeItem();
  }

  /**
   * @description 檢查 Id Token 是否過期
   */
  checkIdTokenExpiration(): boolean {
    if (!this.appConfig.getAzureAD().enabled) {
      return false;
    }

    const idTokenObj = this.authService.instance.getActiveAccount()
      ?.idTokenClaims as any;

    if (isNullOrEmpty(idTokenObj)) {
      return true;
    }

    const now = new Date();
    const expiryTime = subMinutes(fromUnixTime(idTokenObj.exp), 5);

    return now > expiryTime;
  }
}
